// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * AcceptTeamInvite — #/accept-team-invite?token=… (clinic-team invite, leg 2).
 *
 * The emailed invitation (ada-core clinicTeamInvite.ts) lands here. The
 * invited person signs in AS THEMSELVES and accepts; only then does their
 * account become a sub-account of the inviting master.
 *
 * MUST render INSTEAD of the react-admin <Admin> (see the App.tsx gate, same
 * architecture as auth/SsoLanding.tsx) — for two reasons:
 *   1. The common mis-registration case (front-desk registered a pet-OWNER
 *      account, e.g. Palmer 2026-07-10) has role 'user', which vetpo's login
 *      role gate rejects — such a person can NEVER reach a route inside the
 *      Admin. Accepting is exactly what converts them to a vet sub-account,
 *      so the acceptance surface must sit outside the gate.
 *   2. The Admin's boot-time anonymous /me probes fire logout() and would
 *      race any session this page establishes (the SsoLanding lesson).
 *
 * The login here deliberately bypasses authProvider.login (whose role gate
 * would refuse role-user accounts); after a successful accept the server has
 * promoted the account, and applyExternalAuthResult() runs the NORMAL gate on
 * the post-accept identity before handing over to the Admin.
 *
 * Copy follows the browser-detected locale (pos.team_invite.*, en + zh): this page renders
 * before <Admin>, so App.tsx wraps it in I18nContextProvider with the app's own provider.
 * (The invitation EMAIL itself is still sent in English by ada-core.)
 */
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { useTranslate } from 'react-admin'
import AuthShell from '../auth/AuthShell'
import { applyExternalAuthResult } from '../authProvider'
import { deadlineFetch } from '../hooks/customEndpointFetch'

interface InviteClaims {
  masterName?: string
  clinicName?: string
  email?: string
  exp?: number
}

// Display-only decode of the token's JSON body. The SIGNATURE is verified
// server-side at accept — nothing security-relevant happens off this parse.
export function decodeInviteClaims(token: string): InviteClaims | null {
  try {
    const body = token.split('.')[0]
    const claims = JSON.parse(atob(body.replace(/-/g, '+').replace(/_/g, '/')))
    return claims && typeof claims === 'object' ? claims : null
  } catch {
    return null
  }
}

const AcceptTeamInvite: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const translate = useTranslate()
  const token = useMemo(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1] ?? '')
    return params.get('token') ?? ''
  }, [])
  const claims = useMemo(() => (token ? decodeInviteClaims(token) : null), [token])
  const expired = claims?.exp != null && claims.exp * 1000 < Date.now()

  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  const clinicLabel =
    claims?.clinicName || claims?.masterName || translate('pos.team_invite.clinic_fallback')

  const finish = (hash: string) => {
    window.history.replaceState(null, '', `${window.location.pathname}#${hash}`)
    onDone()
  }

  const handleAccept = async () => {
    setBusy(true)
    setError('')
    try {
      // 1. Sign in as the INVITED account (email pinned — consent binding is
      //    re-verified server-side against the token's email claim).
      const loginRes = await deadlineFetch('/api/users/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: claims?.email, password }),
      })
      if (!loginRes.ok) {
        setError('pos.team_invite.sign_in_failed')
        return
      }
      const { token: authToken } = await loginRes.json()

      // 2. Accept — the server re-checks every precondition and links the
      //    account (promoting a mis-registered owner account to vet).
      const acceptRes = await deadlineFetch('/api/users/accept-team-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `JWT ${authToken}` },
        body: JSON.stringify({ token }),
      })
      const acceptBody = await acceptRes.json().catch(() => ({}))
      if (!acceptRes.ok) {
        setError(acceptBody?.errors?.[0]?.message || 'pos.team_invite.accept_failed')
        return
      }

      // 3. Enter the portal with the POST-accept identity (role may have just
      //    been promoted user→vet); the normal role gate applies.
      const meRes = await deadlineFetch('/api/users/me', {
        headers: { Authorization: `JWT ${authToken}` },
      })
      const me = (await meRes.json().catch(() => ({})))?.user
      try {
        applyExternalAuthResult(authToken, me)
      } catch {
        // Gate refused (shouldn't happen post-promotion) — the link still
        // succeeded; send them to login to enter normally.
        setDone(true)
        return
      }
      setDone(true)
      window.setTimeout(() => finish('/'), 1200)
    } catch {
      setError('pos.team_invite.generic_error')
    } finally {
      setBusy(false)
    }
  }

  const invalid = !token || !claims || !claims.email

  return (
    <AuthShell title={translate('pos.team_invite.title')}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {invalid || expired ? (
          <>
            <Alert severity="error" data-testid="invite-error">
              {expired
                ? translate('pos.team_invite.expired')
                : translate('pos.team_invite.invalid')}
            </Alert>
            <Button variant="contained" onClick={() => finish('/login')}>
              {translate('pos.team_invite.go_to_sign_in')}
            </Button>
          </>
        ) : done ? (
          <Alert severity="success" data-testid="invite-success">
            {translate('pos.team_invite.joined', { clinic: clinicLabel })}
          </Alert>
        ) : (
          <>
            <Typography variant="body2">
              {translate('pos.team_invite.intro', { clinic: clinicLabel, email: claims.email })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {translate('pos.team_invite.confirm_it_is_you')}
            </Typography>
            <TextField
              label={translate('pos.team_invite.email')}
              value={claims.email}
              disabled
              fullWidth
              size="small"
            />
            <TextField
              label={translate('pos.team_invite.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              size="small"
              autoFocus
            />
            {error && (
              <Alert severity="error" data-testid="invite-accept-error">
                {translate(error, { _: error })}
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleAccept}
                disabled={busy || !password}
                data-testid="invite-accept-button"
              >
                {busy ? <CircularProgress size={20} /> : translate('pos.team_invite.accept')}
              </Button>
              <Button variant="text" onClick={() => finish('/login')} disabled={busy}>
                {translate('pos.team_invite.decline')}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {translate('pos.team_invite.forgot_before')}{' '}
              {/* biome-ignore lint/a11y/useValidAnchor: deliberate hash-route
                    anchor — the onClick forces the SPA to reload into the
                    forgot-password route, which a button cannot do here */}
              <a
                href="#/forgot-password"
                onClick={() => window.setTimeout(() => window.location.reload(), 0)}
              >
                {translate('pos.team_invite.forgot_link')}
              </a>{' '}
              {translate('pos.team_invite.forgot_after')}
            </Typography>
          </>
        )}
      </Box>
    </AuthShell>
  )
}

export default AcceptTeamInvite
